import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "./useAuth";

export interface TrackingSession {
  id: number;
  status: "RUNNING" | "COMPLETED" | "FAILED";
  startedAt: string;
  completedAt?: string;
  totalAwbsChecked: number;
  totalStatusesChanged: number;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SessionsListResponse {
  sessions: TrackingSession[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UseAwbTrackingReturn {
  currentSession: TrackingSession | null;
  isTracking: boolean;
  error: string | null;
  startTracking: () => Promise<void>;
  stopPolling: () => void;
  fetchRecentSessions: (
    page?: number,
    limit?: number
  ) => Promise<SessionsListResponse>;
  clearError: () => void;
}

export function useAwbTracking(): UseAwbTrackingReturn {
  const [currentSession, setCurrentSession] = useState<TrackingSession | null>(
    null
  );
  const [isTracking, setIsTracking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsTracking(false);
  }, []);

  const pollSession = useCallback(
    async (sessionId: number) => {
      try {
        const response = await fetchWithAuth(
          `/api/admin/shipping/tracking/sessions/${sessionId}`
        );

        if (!response.ok) {
          throw new Error(
            `Eroare la obținerea statusului sesiunii: ${response.status}`
          );
        }

        const session: TrackingSession = await response.json();
        setCurrentSession(session);

        // Stop polling if session is no longer running
        if (session.status !== "RUNNING") {
          stopPolling();
        }
      } catch (err) {
        console.error("Polling error:", err);
        // Don't set error state for temporary polling failures
        // The polling will continue and might recover
      }
    },
    [stopPolling]
  );

  const startTracking = useCallback(async () => {
    try {
      setError(null);
      setIsTracking(true);

      // Start tracking session
      const response = await fetchWithAuth(
        "/api/admin/shipping/tracking/start",
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Eroare la pornirea tracking-ului: ${response.status}`
        );
      }

      const { sessionId } = await response.json();

      // Create initial session object
      setCurrentSession({
        id: sessionId,
        status: "RUNNING",
        startedAt: new Date().toISOString(),
        totalAwbsChecked: 0,
        totalStatusesChanged: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Start polling for session status
      pollingIntervalRef.current = setInterval(() => {
        pollSession(sessionId);
      }, 30000); // Poll every 30 seconds

      // Initial poll
      await pollSession(sessionId);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Eroare necunoscută la pornirea tracking-ului";
      setError(errorMessage);
      setIsTracking(false);
      setCurrentSession(null);
    }
  }, [pollSession]);

  const fetchRecentSessions = useCallback(
    async (page = 1, limit = 20): Promise<SessionsListResponse> => {
      try {
        const response = await fetchWithAuth(
          `/api/admin/shipping/tracking/sessions?page=${page}&limit=${limit}`
        );

        if (!response.ok) {
          throw new Error(`Eroare la obținerea sesiunilor: ${response.status}`);
        }

        return await response.json();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Eroare la obținerea sesiunilor";
        setError(errorMessage);
        throw err;
      }
    },
    []
  );

  return {
    currentSession,
    isTracking,
    error,
    startTracking,
    stopPolling,
    fetchRecentSessions,
    clearError,
  };
}
